/**
 * 山形県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は福岡・北海道と同じテンプレート。
 * 進行の軸は「物理的な大きさ ＝ ごちそう度」で、県ごとに触らない。
 *
 * 「書いてはいけないこと」（claude/山形データシート案.md）を反映している。
 *  - 「日本三大和牛」は書かない。選定主体が公式資料のどこにも無い。
 *    代わりに地理的表示（GI）登録 第26号 を書く。こちらのほうが強く、かつ正確。
 *  - だだちゃ豆の GI・商標には触れない（GI未登録。商標は裏が取れなかった）。
 *  - 冷たい肉そばの「発祥」は断定しない（河北町公式自身が伝聞形）。
 *  - 芋煮の起源は断定しない。内陸＝牛肉醤油／庄内＝豚肉味噌の対比だけ農水省が明記。
 *  - 「だし」は県内全域ではなく村山地域中心。
 *  - 正式名称は「鯉のうま煮」（甘煮ではない）、「どんがら汁／寒鱈汁」。
 *
 * 本番画像は public/assets/foods/yamagata/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：どんがら汁（店で食べられる品に差し替え）
 * - げそ天：「山形のそば屋の定番の添え物」「げそ天もりそば」などの言い切り：出典は「しばしば出会う」までなので、定番とは書かない
 * - げそ天：発祥の店・年（昭和40年代に◯◯が最初、など）：まとめサイト・企業の記事にしかなく、公式で確かめられないので書かない
 * - げそ天：「内陸でイカが手に入りにくかったから」という由来：公式資料で確かめられないので書かない
 * - げそ天：スーパーの総菜としてのげそ天（店名が出る）：特定の店の話になるので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { YAMAGATA_FOOD_HULLS } from './yamagataFoodShapes.generated';
import { yamagataSceneryTour } from './yamagataScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'sato-nishiki',
    level: 1,
    name: 'さくらんぼ（佐藤錦）',
    shortName: 'さくらんぼ',
    image: '/assets/foods/yamagata/01_sato_nishiki.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '山形を代表する赤い宝石。',
    source:
      'https://www.pref.yamagata.jp/020026/kensei/shoukai/yamagatamonogatari/sangyou/sakuranbo.html',
    icon: { label: '桜桃', shape: 'berry', baseColor: '#d8323f', accentColor: '#6fa84f' },
    reward: {
      unlockScore: 200,
      area: '寒河江・東根',
      headline: 'さくらんぼの生産量は全国1位。全国のおよそ7割が山形県産。',
      tip: '旬は6月。「佐藤錦」は東根市の佐藤栄助が育て、苗木商の岡田東作がその名をつけた。',
      links: searchLinks('山形県', 'さくらんぼ狩り'),
    },
  },
  {
    id: 'dadachamame',
    level: 2,
    name: 'だだちゃ豆',
    shortName: 'だだちゃ豆',
    image: '/assets/foods/yamagata/02_dadachamame.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '香りの強い庄内の枝豆。',
    source: 'https://yamagatakanko.com/local-foods',
    icon: { label: '枝豆', shape: 'roe', baseColor: '#8fb349', accentColor: '#4a6b28' },
    reward: {
      unlockScore: 500,
      area: '鶴岡',
      headline: '鶴岡市の一部でだけ栽培される、香りの強いブランド枝豆。',
      tip: '旬は8月中旬から下旬のごく短い間。ゆでている途中から香りが立つ。',
      links: searchLinks('鶴岡市', 'だだちゃ豆'),
    },
  },
  {
    id: 'tama-konnyaku',
    level: 3,
    name: '玉こんにゃく',
    shortName: '玉こん',
    image: '/assets/foods/yamagata/03_tama_konnyaku.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '串に刺した醤油味の玉こんにゃく。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/tamakonnyaku_yamagata.html',
    icon: { label: '玉こん', shape: 'feast', baseColor: '#6b5540', accentColor: '#d9c58a' },
    reward: {
      unlockScore: 1000,
      area: '山形市・山寺',
      headline: '農林水産省によると、山形県はこんにゃくの消費量が日本一。1家庭あたり年間およそ10kg。',
      tip: 'からしを少し。山寺の石段のぼりの途中で食べるのが山形流。',
      links: searchLinks('山形市 山寺', '玉こんにゃく'),
    },
  },
  {
    id: 'dondon-yaki',
    level: 4,
    name: 'どんどん焼き',
    shortName: 'どんどん焼き',
    image: '/assets/foods/yamagata/04_dondon_yaki.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '割り箸に巻いた薄焼きの粉もの。',
    source:
      'https://www.kankou.yamagata.yamagata.jp/db/cgi-bin/search/search.cgi?panel=detail&d01=126645458535136&c=20',
    icon: { label: '粉物', shape: 'mochi', baseColor: '#e0b46a', accentColor: '#7a4a22' },
    reward: {
      unlockScore: 1800,
      area: '山形市',
      headline: '水で溶いた生地を鉄板で薄くのばし、割り箸にくるくると巻く。',
      tip: 'ソース味と醤油味がある。もっちりした食感が持ち味。',
      links: searchLinks('山形市', 'どんどん焼き'),
    },
  },
  {
    id: 'dashi',
    level: 5,
    name: '山形のだし',
    shortName: 'だし',
    image: '/assets/foods/yamagata/05_dashi.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '刻んだ夏野菜をごはんにのせて。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/dashi_yamagata.html',
    icon: { label: 'だし', shape: 'rice-bowl', baseColor: '#8fb349', accentColor: '#f2ead6' },
    reward: {
      unlockScore: 3000,
      area: '村山地域',
      headline: '夏野菜を生のまま刻んで和えるだけ。暑い日でも箸が進む。',
      tip: 'ごはんにも冷奴にもそうめんにも。村山地域を中心に食べられてきた。',
      links: searchLinks('山形 村山', 'だし 郷土料理'),
    },
  },
  {
    id: 'tsumetai-nikusoba',
    level: 6,
    name: '冷たい肉そば',
    shortName: '肉そば',
    image: '/assets/foods/yamagata/06_tsumetai_nikusoba.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '冷たい鶏だしの田舎そば。',
    source: 'https://www.town.kahoku.yamagata.jp/soshiki/shoko/kankousinkou/3341/672.html',
    icon: { label: '肉そば', shape: 'noodle-bowl', baseColor: '#c8a15c', accentColor: '#6b4a2f' },
    reward: {
      unlockScore: 4500,
      area: '河北町・谷地',
      headline: 'コシの強い田舎そばに、冷たい鶏ガラの甘じょっぱいつゆ。',
      tip: '具は親鶏のチャーシューとネギ。文化庁の「100年フード」に認定されている。',
      links: searchLinks('河北町 谷地', '冷たい肉そば'),
    },
  },
  {
    id: 'geso-ten',
    level: 7,
    name: 'げそ天',
    shortName: 'げそ天',
    image: '/assets/foods/yamagata/07_geso_ten.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: 'イカゲソの天ぷら。山形市の飾らない庶民派の蕎麦屋では、イカゲソ天がついてくる蕎麦のメニューにしばしば出会う。山形市は「げそ天ワンダーランド」とも紹介されている。',
    source:
      'https://www.portal-yamagata.jp/spot/188',
    icon: { label: 'げそ天', shape: 'dumpling', baseColor: '#e7c27a', accentColor: '#b5652e' },
    reward: {
      unlockScore: 6500,
      area: '山形市ほか村山地域',
      headline: '山形市の蕎麦屋でよく出会う天ぷら',
      tip: 'メニューの名前は「げそ天そば」「もり天」など、店によってひとつではない。庶民派の蕎麦屋でメニューをよく見ると見つかる。',
      links: searchLinks('山形市', 'げそ天 そば'),
      official: [{ label: '大石田町「大石田そば街道名店案内」', url: 'https://www.town.oishida.yamagata.jp/kankou/sobakaidou/sobakaido1.html' }],
    },
  },
  {
    id: 'imoni',
    level: 8,
    name: '芋煮',
    shortName: '芋煮',
    image: '/assets/foods/yamagata/08_imoni.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '里芋を主役にした山形の鍋。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/imoni_yamagata.html',
    icon: { label: '芋煮', shape: 'hotpot', baseColor: '#8a5a32', accentColor: '#c9b48a' },
    reward: {
      unlockScore: 9000,
      area: '県内全域',
      headline: '内陸は牛肉の醤油味、庄内は豚肉の味噌味。同じ県でまったく違う。',
      tip: '9月中旬、山形市の河川敷で直径6.5mの大鍋を使う「日本一の芋煮会フェスティバル」が開かれる。',
      links: searchLinks('山形県', '芋煮'),
    },
  },
  {
    id: 'ita-soba',
    level: 9,
    name: '板そば',
    shortName: '板そば',
    image: '/assets/foods/yamagata/09_ita_soba.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '長い木箱に盛った太めのそば。',
    source: 'https://www.maff.go.jp/j/keikaku/syokubunka/traditional-foods/menu/yamagata_soba.html',
    icon: { label: '板そば', shape: 'feast', baseColor: '#c9b48a', accentColor: '#5a4630' },
    reward: {
      unlockScore: 12000,
      area: '村山・尾花沢',
      headline: '長い板や木箱でそばを出した「そば振る舞い」の風習に由来する。',
      tip: '何人かで囲んで分けるのが本来の姿。内陸部で食べ継がれてきた。',
      links: searchLinks('山形 村山', '板そば'),
    },
  },
  {
    id: 'koi-no-umani',
    level: 10,
    name: '鯉のうま煮',
    shortName: '鯉のうま煮',
    image: '/assets/foods/yamagata/10_koi_no_umani.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '甘辛く煮つめた米沢の鯉。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/koinomani_yamagata.html',
    icon: { label: '鯉', shape: 'fish', baseColor: '#7a4a2a', accentColor: '#e0b46a' },
    reward: {
      unlockScore: 16000,
      area: '米沢',
      headline: '1802年、米沢藩9代藩主・上杉鷹山が鯉の養殖を勧めたのがはじまり。',
      tip: '酒・醤油・砂糖でとろとろになるまで煮つめる。正月やお祝いの膳に。',
      links: searchLinks('米沢市', '鯉のうま煮'),
    },
  },
  {
    id: 'iwagaki',
    level: 11,
    name: '庄内浜の岩牡蠣',
    shortName: '岩牡蠣',
    image: '/assets/foods/yamagata/11_iwagaki.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '夏が旬の大ぶりな天然牡蠣。',
    source: 'https://yamagatakanko.com/seafoods',
    icon: {
      label: '岩牡蠣',
      shape: 'sashimi-platter',
      baseColor: '#cfd6cf',
      accentColor: '#6b7a6b',
    },
    reward: {
      unlockScore: 22000,
      area: '庄内浜',
      headline: '旬は6月から8月。秋冬が食べごろの真牡蠣とはまったく別もの。',
      tip: 'ほとんどが天然で、素潜り漁でとる。レモンを搾ってそのまま。',
      links: searchLinks('庄内 酒田 鶴岡', '岩牡蠣'),
    },
  },
  {
    id: 'yonezawa-gyu',
    level: 12,
    name: '米沢牛のすき焼き',
    shortName: '米沢牛',
    image: '/assets/foods/yamagata/12_yonezawa_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '霜降りの米沢牛を割下で。山形の冬のごちそう。',
    source: 'https://www.maff.go.jp/j/shokusan/gi_act/register/0026/index.html',
    icon: {
      label: '米沢牛',
      shape: 'curry-plate',
      baseColor: '#c14a4a',
      accentColor: '#f4e6d2',
    },
    reward: {
      unlockScore: 30000,
      area: '米沢',
      headline: '地理的表示（GI）登録 第26号。登録は平成29年3月3日。',
      tip: '米沢は「すき焼きのまち」。米沢駅の米沢牛駅弁は120年続くロングセラー。',
      links: searchLinks('米沢市', '米沢牛 すき焼き'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const yamagataFoodStage: StageDefinition = {
  regionId: 'yamagata',
  regionName: '山形県',
  mode: 'food',
  title: '山形めしマージ',
  theme: {
    primaryColor: '#b23a48',
    secondaryColor: '#d9a441',
    accentColor: '#f2d16b',
    backgroundColor: '#1f1b1a',
  },
  /** 背景ツアー「山形めぐり」。北海道と同じく軸は季節。5月から翌4月へ一周する。 */
  scenery: {
    tour: yamagataSceneryTour,
  },
  // 画像の輪郭（自動生成）を付与する。
  items: attachHulls(FOOD_ITEMS, YAMAGATA_FOOD_HULLS),
};
